/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
// React
import { useState, useEffect } from 'react';

// Mantine
import { Box, Group, Stack } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

// Components
import {
	Link,
	TextInput,
	Text,
	Select,
} from '@components/atoms';
import { MultiSelect } from '@components/molecules';

// i18n
import { useTranslation } from 'react-i18next';

// Styles
import classes from './UpdateMeterPointsForm.module.css';

// Store
import useStore from '@store/index';

// Types
import {
	TProduction,
	TSelectOption,
} from '@global-types/index';
import { EUROPEAN_COUNTRIES } from '@config/constants/european-countries';

const options: TSelectOption[] = [
	{ label: 'global.place', value: 'place' },
];

/* -------------------------------------------------------------------------- */
/*                                     Type                                   */
/* -------------------------------------------------------------------------- */
type TSingleFormMeterPointProps = {
	item: TProduction;
	index: number;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	form: any;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const SingleFormMeterPoint = (
	props: TSingleFormMeterPointProps
) => {
	const isBigScreen = useMediaQuery('(min-width: 120em)');
	const { t } = useTranslation();

	/* ******************************** STATES ******************************* */

	const {
		meterPointsDefaultSelectedFields,
		setMeterPointsDefaultSelectedFields,
	} = useStore();

	const [activeAppliedFields, setActiveAppliedFields] =
		useState<TSelectOption[] | []>([]);

	useEffect(() => {
		if (activeAppliedFields && props.index === 0) {
			setMeterPointsDefaultSelectedFields(
				activeAppliedFields
			);
		}
	}, [activeAppliedFields]);

	useEffect(() => {
		setActiveAppliedFields(
			meterPointsDefaultSelectedFields
		);
	}, []);

	/* ******************************** RENDERING ******************************* */
	return (
		<Stack key={props.item.id}>
			<Box className={classes.rowHeader}>
				<Group>
					<Text className={classes.titleItem}>
						{t('global.meterPoint')}
					</Text>
				</Group>
				{props.index !== 0 && (
					<Link
						size="sm"
						onClick={() =>
							props.form.removeListItem(
								'meterPoints',
								props.index
							)
						}
						c="error"
						underline="always"
					>
						{t('meterPoints.deleteMeterPoint')}
					</Link>
				)}
			</Box>
			<Box className={classes.row}>
				<Box className={classes.containerInput}>
					<TextInput
						withAsterisk
						size={isBigScreen ? 'md' : 'sm'}
						label={t('global.serialNumber')}
						placeholder={'uuid'}
						{...props.form.getInputProps(
							`meterPoints.${props.index}.serialNumber`
						)}
					/>
				</Box>
			</Box>
			<Box className={classes.row}>
				{/* Country code */}
				<Box className={classes.containerInput}>
					<Select
						size={isBigScreen ? 'md' : 'sm'}
						label={t('global.country')}
						withAsterisk
						data={EUROPEAN_COUNTRIES}
						placeholder={t('global.selectEntity')}
						{...props.form.getInputProps(
							`meterPoints.${props.index}.location.countryCode`
						)}
					/>
				</Box>
				{/* Place */}
				<Box className={classes.containerInput}>
					<TextInput
						size={isBigScreen ? 'md' : 'sm'}
						label={t('global.place')}
						placeholder={t('global.place')}
						{...props.form.getInputProps(
							`meterPoints.${props.index}.location.place`
						)}
					/>
				</Box>
			</Box>
		</Stack>
	);
};

export default SingleFormMeterPoint;
